// SCR-STAFF-904 (AP-PLT-07): وضعیت اشتراک و زمان‌بندی Trial و عدم پرداخت، خرید و تمدید با پیش‌فاکتور (US-PLT-022)، تغییر پلن با مابه‌التفاوت روزشمار یا
// مغایرت سقف‌ها (US-PLT-023)، صورتحساب‌ها با PDF (US-PLT-024) و محیط نمایشی (US-PLT-053). پرداخت در ReadOnly و Locked هم ممکن است (BR-PLT-006)؛ درگاه
// نمونه همیشه با نشان «نمونه» (CLAUDE.md §10) و در نبود درگاه دکمه پرداخت غیرفعال با پیام (C-AGENT-1154).
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type BillingCycle,
  type SubscriptionInvoiceView,
  type SubscriptionOverview,
  type SubscriptionPaymentStart,
  type SubscriptionPlanChangePreview,
  type SubscriptionQuote,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  KeyValueList,
  LoadingState,
  Money,
  SampleBadge,
  StatusChip,
  toJalali,
  useMoneyFormatter,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { saveBlob } from '../medorders/common';
import { toCount } from './common';

export const subscriptionKeys = {
  overview: (tenantId: string | undefined) => ['platform', 'subscription', tenantId] as const,
  invoices: (tenantId: string | undefined) => ['platform', 'subscription', 'invoices', tenantId] as const,
  demo: (tenantId: string | undefined) => ['platform', 'tenant', 'demo-environment', tenantId] as const,
};

const MANAGE = 'plt.subscription.manage';

function statusTone(status: string): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Trial':
    case 'PendingPayment':
    case 'PastDue':
      return 'pending';
    case 'TrialExpired':
    case 'Lapsed':
      return 'error';
    default:
      return 'inactive';
  }
}

function lastDay(date: string | null | undefined) {
  return date ? toJalali(date) : '—';
}

/** پرداخت آغازشده: انتقال به درگاه یا تسویه بی‌درگاه. */
function usePaymentRedirect(onSettled: () => void) {
  return (start: SubscriptionPaymentStart) => {
    if (start.redirectUrl) {
      window.location.assign(start.redirectUrl);
      return;
    }
    onSettled();
  };
}

export function SubscriptionPlanSection({ overview }: { overview: SubscriptionOverview }) {
  const { t } = useTranslation('plt');
  const queryClient = useQueryClient();
  const { tenantApi } = useTenantSession();
  const access = useAccess();
  const canManage = access.can(MANAGE);
  const money = useMoneyFormatter();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['platform', 'subscription'] });
  const redirect = usePaymentRedirect(invalidate);
  const autoRenew = useMutation({
    mutationFn: (enabled: boolean) => unwrap(tenantApi.PUT('/api/v1/platform/subscription/auto-renew', { body: { enabled } })),
    onSuccess: invalidate,
  });
  const payOpen = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/platform/subscription/invoices/{id}:pay', { params: { path: { id } } })),
    onSuccess: redirect,
  });

  const open = overview.openInvoice;
  const restricted = overview.accessMode !== 'Full';
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="subscription-plan">
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
        <Typography variant="h6">{t('plt:subscription.plan_title')}</Typography>
        <StatusChip tone={statusTone(overview.status)} labelKey={`plt:subscription.status.${overview.status}`} />
      </Stack>
      <Stack spacing={2}>
        <KeyValueList
          columns={3}
          items={[
            { labelKey: 'plt:subscription.fields.plan', value: <bdi dir="ltr">{overview.planCode}</bdi>, testId: 'subscription-plan-code' },
            { labelKey: 'plt:subscription.fields.cycle', value: overview.cycle ? t(`plt:subscription.cycle.${overview.cycle}`) : '—' },
            {
              labelKey: 'plt:subscription.fields.period',
              value: overview.periodStart
                ? t('plt:subscription.period_value', { start: lastDay(overview.periodStart), end: lastDay(overview.periodEnd) })
                : '—',
              testId: 'subscription-period',
            },
            { labelKey: 'plt:subscription.fields.days_left', value: overview.daysLeft === null ? '—' : faDigits(toCount(overview.daysLeft)) },
            {
              labelKey: 'plt:subscription.fields.providers',
              value: `${faDigits(toCount(overview.paidProviders))} / ${faDigits(toCount(overview.activeProviders))}`,
            },
            {
              labelKey: 'plt:subscription.fields.access_mode',
              value: t(`plt:subscription.access_mode.${overview.accessMode}`),
              testId: 'subscription-access-mode',
            },
          ]}
        />
        {overview.status === 'Trial' ? (
          <Alert severity="info" data-testid="subscription-trial-notice">
            {t('plt:subscription.trial_notice', { date: lastDay(overview.periodEnd), days: faDigits(toCount(overview.daysLeft)) })}
          </Alert>
        ) : null}
        {overview.status === 'TrialExpired' ? (
          <Alert severity="warning" data-testid="subscription-trial-expired">
            {t('plt:subscription.trial_expired_notice', { date: lastDay(overview.deleteOn) })}
          </Alert>
        ) : null}
        {overview.status === 'PastDue' ? (
          <Alert severity="warning" data-testid="subscription-past-due">
            {t('plt:subscription.past_due_notice', { readOnly: lastDay(overview.readOnlyFrom), locked: lastDay(overview.lockedFrom) })}
          </Alert>
        ) : null}
        {overview.status === 'Lapsed' ? (
          <Alert severity="error" data-testid="subscription-lapsed">
            {t('plt:subscription.lapsed_notice', {
              mode: t(`plt:subscription.access_mode.${overview.accessMode}`),
              locked: lastDay(overview.lockedFrom),
            })}
          </Alert>
        ) : null}
        {restricted && overview.accessModeReason === 'identity_unverified' ? (
          <Alert severity="error">{t('plt:subscription.identity_notice')}</Alert>
        ) : null}
        {overview.pendingPlanCode ? (
          <Alert severity="info" data-testid="subscription-pending-change">
            {t('plt:subscription.change.pending', { plan: overview.pendingPlanCode })}
          </Alert>
        ) : null}
        {open ? (
          <Alert
            severity="warning"
            data-testid="subscription-open-invoice"
            action={
              canManage ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={!overview.onlinePaymentAvailable || payOpen.isPending}
                  onClick={() => payOpen.mutate(open.id)}
                  data-testid="subscription-pay-open"
                >
                  {t('plt:subscription.pay_invoice')}
                </Button>
              ) : undefined
            }
          >
            {t('plt:subscription.open_invoice', {
              number: open.number,
              amount: money(open.total.amount, open.total.currency),
              due: toJalali(open.dueOn),
            })}
          </Alert>
        ) : null}
        {payOpen.error ? <ErrorState error={payOpen.error} compact /> : null}
        {overview.isPaidPeriod ? (
          <FormControlLabel
            control={
              <Switch
                checked={overview.autoRenew}
                disabled={!canManage || autoRenew.isPending}
                onChange={(_, checked) => autoRenew.mutate(checked)}
                inputProps={{ 'aria-label': t('plt:subscription.auto_renew') }}
                data-testid="subscription-auto-renew"
              />
            }
            label={t('plt:subscription.auto_renew')}
          />
        ) : null}
      </Stack>
    </Paper>
  );
}

function QuoteTable({ quote }: { quote: SubscriptionQuote }) {
  const { t } = useTranslation('plt');
  return (
    <Stack spacing={1} data-testid="subscription-quote">
      <Typography variant="subtitle1">{t('plt:subscription.quote.title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('plt:subscription.quote.period', { start: toJalali(quote.periodStart), end: toJalali(quote.periodEnd) })}
      </Typography>
      <Table size="small" aria-label={t('plt:subscription.quote.title')}>
        <TableBody>
          {quote.lines.map((line, index) => (
            <TableRow key={`${line.kind}-${line.code}-${index}`} data-testid={`quote-line-${line.kind}`}>
              <TableCell>
                {t(`plt:subscription.quote.line.${line.kind}`, {
                  count: toCount(line.quantity),
                  code: line.code,
                  months: faDigits(toCount(line.months)),
                  days: faDigits(toCount(line.days)),
                  period: faDigits(toCount(line.periodDays)),
                })}
              </TableCell>
              <TableCell align="right">
                <Money amountMinor={line.amount.amount} currency={line.amount.currency} />
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell>{t('plt:subscription.quote.net')}</TableCell>
            <TableCell align="right">
              <Money amountMinor={quote.net.amount} currency={quote.net.currency} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t('plt:subscription.quote.vat', { rate: faDigits(toCount(quote.vatRateBasisPoints) / 100) })}</TableCell>
            <TableCell align="right">
              <Money amountMinor={quote.vat.amount} currency={quote.vat.currency} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{t('plt:subscription.quote.total')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }} data-testid="subscription-quote-total" data-amount={quote.total.amount}>
              <Money amountMinor={quote.total.amount} currency={quote.total.currency} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {quote.sellerIsSample ? <SampleBadge /> : null}
    </Stack>
  );
}

export function PurchaseSection({ overview }: { overview: SubscriptionOverview }) {
  const { t } = useTranslation('plt');
  const { tenantApi } = useTenantSession();
  const access = useAccess();
  const money = useMoneyFormatter();
  const queryClient = useQueryClient();
  const activeProviders = Math.max(1, toCount(overview.activeProviders));
  const [planCode, setPlanCode] = useState(overview.planCode);
  const [cycle, setCycle] = useState<NonNullable<BillingCycle>>(overview.cycle ?? 'Annual');
  const [providers, setProviders] = useState(String(Math.max(activeProviders, toCount(overview.paidProviders))));
  const [addons, setAddons] = useState<string[]>(overview.activeAddons);
  const [quote, setQuote] = useState<SubscriptionQuote>();
  const [settled, setSettled] = useState(false);
  const body = { planCode, cycle, providers: Number(providers) || activeProviders, addons, branches: null };
  const quoteMutation = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/subscription:quote', { body })),
    onSuccess: setQuote,
  });
  const redirect = usePaymentRedirect(() => {
    setSettled(true);
    void queryClient.invalidateQueries({ queryKey: ['platform', 'subscription'] });
  });
  const checkout = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/subscription:checkout', { body })),
    onSuccess: redirect,
  });
  const paidActive = overview.status === 'Active' && overview.isPaidPeriod;
  if (paidActive || !access.can(MANAGE)) {
    return null;
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="subscription-buy">
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('plt:subscription.buy.title')}
      </Typography>
      <Stack spacing={2}>
        {!overview.onlinePaymentAvailable ? (
          <Alert severity="warning" data-testid="subscription-payment-unavailable">
            {t('plt:subscription.payment_unavailable')}
          </Alert>
        ) : overview.paymentIsSample ? (
          <Box>
            <SampleBadge />
          </Box>
        ) : null}
        <TextField
          select
          label={t('plt:subscription.buy.plan')}
          value={planCode}
          onChange={(event) => {
            setPlanCode(event.target.value);
            setQuote(undefined);
          }}
          inputProps={{ 'data-testid': 'subscription-buy-plan' }}
        >
          {overview.plans.map((plan) => (
            <MenuItem key={plan.code} value={plan.code}>
              {t('plt:subscription.buy.plan_option', {
                code: plan.code,
                base: money(plan.baseMonthly.amount, plan.baseMonthly.currency),
                perProvider: money(plan.perProviderMonthly.amount, plan.perProviderMonthly.currency),
              })}
            </MenuItem>
          ))}
        </TextField>
        <FormControl>
          <FormLabel id="subscription-cycle">{t('plt:subscription.buy.cycle')}</FormLabel>
          <RadioGroup
            row
            aria-labelledby="subscription-cycle"
            value={cycle}
            onChange={(event) => {
              setCycle(event.target.value as NonNullable<BillingCycle>);
              setQuote(undefined);
            }}
          >
            <FormControlLabel value="Monthly" control={<Radio />} label={t('plt:subscription.cycle.Monthly')} data-testid="subscription-cycle-monthly" />
            <FormControlLabel
              value="Annual"
              control={<Radio />}
              label={`${t('plt:subscription.cycle.Annual')} — ${t('plt:subscription.buy.annual_hint', { months: faDigits(toCount(overview.annualFreeMonths)) })}`}
              data-testid="subscription-cycle-annual"
            />
          </RadioGroup>
        </FormControl>
        <TextField
          type="number"
          label={t('plt:subscription.buy.providers')}
          helperText={t('plt:subscription.buy.providers_help', { count: faDigits(activeProviders) })}
          value={providers}
          onChange={(event) => {
            setProviders(event.target.value);
            setQuote(undefined);
          }}
          slotProps={{ htmlInput: { min: activeProviders, 'data-testid': 'subscription-buy-providers' } }}
        />
        {overview.addons.length > 0 ? (
          <FormControl component="fieldset">
            <FormLabel component="legend">{t('plt:subscription.buy.addons')}</FormLabel>
            <FormGroup>
              {overview.addons.map((addon) => (
                <FormControlLabel
                  key={addon.code}
                  control={
                    <Checkbox
                      checked={addons.includes(addon.code)}
                      onChange={(_, checked) => {
                        setAddons(checked ? [...addons, addon.code] : addons.filter((code) => code !== addon.code));
                        setQuote(undefined);
                      }}
                    />
                  }
                  label={t('plt:subscription.buy.addon_option', { code: addon.code, price: money(addon.monthly.amount, addon.monthly.currency) })}
                />
              ))}
            </FormGroup>
          </FormControl>
        ) : null}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => quoteMutation.mutate()} disabled={quoteMutation.isPending} data-testid="subscription-quote-submit">
            {t('plt:subscription.buy.quote')}
          </Button>
          <Button
            variant="contained"
            onClick={() => checkout.mutate()}
            disabled={!quote || !overview.onlinePaymentAvailable || checkout.isPending}
            data-testid="subscription-pay"
          >
            {t('plt:subscription.buy.pay')}
          </Button>
        </Stack>
        {quoteMutation.error ? <ErrorState error={quoteMutation.error} compact /> : null}
        {checkout.error ? <ErrorState error={checkout.error} compact /> : null}
        {settled ? <Alert severity="success">{t('plt:subscription.buy.settled')}</Alert> : null}
        {quote ? <QuoteTable quote={quote} /> : null}
      </Stack>
    </Paper>
  );
}

export function PlanChangeSection({ overview }: { overview: SubscriptionOverview }) {
  const { t } = useTranslation('plt');
  const { tenantApi } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState<SubscriptionPlanChangePreview>();
  const [scheduled, setScheduled] = useState(false);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['platform', 'subscription'] });
  const redirect = usePaymentRedirect(invalidate);
  const previewMutation = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/subscription:preview-change', { body: { planCode: target } })),
    onSuccess: setPreview,
  });
  const change = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/subscription:change-plan', { body: { planCode: target } })),
    onSuccess: (result) => {
      if (result.payment) {
        redirect(result.payment);
        return;
      }
      setScheduled(true);
      setPreview(undefined);
      invalidate();
    },
  });
  const cancel = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/subscription:cancel-change')),
    onSuccess: invalidate,
  });
  if (!(overview.status === 'Active' && overview.isPaidPeriod) || !access.can(MANAGE)) {
    return null;
  }
  const blocked = (preview?.discrepancies.length ?? 0) > 0;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="subscription-change">
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('plt:subscription.change.title')}
      </Typography>
      <Stack spacing={2}>
        {overview.pendingPlanCode ? (
          <Button variant="outlined" color="warning" onClick={() => cancel.mutate()} disabled={cancel.isPending} data-testid="subscription-cancel-change" sx={{ alignSelf: 'flex-start' }}>
            {t('plt:subscription.change.cancel')}
          </Button>
        ) : null}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            select
            label={t('plt:subscription.change.target')}
            value={target}
            onChange={(event) => {
              setTarget(event.target.value);
              setPreview(undefined);
            }}
            sx={{ minWidth: 220 }}
            inputProps={{ 'data-testid': 'subscription-change-target' }}
          >
            {overview.plans
              .filter((plan) => plan.code !== overview.planCode)
              .map((plan) => (
                <MenuItem key={plan.code} value={plan.code}>
                  <bdi dir="ltr">{plan.code}</bdi>
                </MenuItem>
              ))}
          </TextField>
          <Button variant="outlined" onClick={() => previewMutation.mutate()} disabled={!target || previewMutation.isPending} data-testid="subscription-change-preview">
            {t('plt:subscription.change.preview')}
          </Button>
        </Stack>
        {previewMutation.error ? <ErrorState error={previewMutation.error} compact /> : null}
        {preview ? (
          <Stack spacing={1} data-testid="subscription-change-result" data-kind={preview.kind}>
            <Alert severity={blocked ? 'error' : 'info'}>
              {blocked
                ? t('plt:subscription.change.discrepancies')
                : t(preview.kind === 'Upgrade' ? 'plt:subscription.change.upgrade' : 'plt:subscription.change.downgrade', {
                    plan: preview.targetPlanCode,
                    date: toJalali(preview.effectiveOn),
                  })}
            </Alert>
            {blocked ? (
              <Box component="ul" sx={{ m: 0 }} data-testid="subscription-discrepancies">
                {preview.discrepancies.map((item) => (
                  <li key={item.limitKey}>
                    {t('plt:subscription.change.discrepancy', {
                      limit: t(`plt:subscription.limits.${item.limitKey.replace('.', '_')}`),
                      current: faDigits(toCount(item.current)),
                      max: faDigits(toCount(item.max)),
                    })}
                  </li>
                ))}
              </Box>
            ) : null}
            {preview.quote ? <QuoteTable quote={preview.quote} /> : null}
            {!blocked ? (
              <Button
                variant="contained"
                onClick={() => change.mutate()}
                disabled={change.isPending || (preview.kind === 'Upgrade' && !overview.onlinePaymentAvailable)}
                data-testid="subscription-change-confirm"
                sx={{ alignSelf: 'flex-start' }}
              >
                {t(preview.kind === 'Upgrade' ? 'plt:subscription.change.confirm_upgrade' : 'plt:subscription.change.confirm_downgrade')}
              </Button>
            ) : null}
          </Stack>
        ) : null}
        {change.error ? <ErrorState error={change.error} compact /> : null}
        {scheduled ? <Alert severity="success">{t('plt:subscription.change.scheduled')}</Alert> : null}
      </Stack>
    </Paper>
  );
}

export function InvoicesSection() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const invoices = useQuery({
    queryKey: subscriptionKeys.invoices(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/subscription/invoices')),
  });
  const download = async (invoice: SubscriptionInvoiceView) => {
    const result = await tenantApi.GET('/api/v1/platform/subscription/invoices/{id}/pdf', {
      params: { path: { id: invoice.id } },
      parseAs: 'blob',
    });
    if (result.response.ok) {
      saveBlob(result.data as unknown as Blob, `${invoice.number}.pdf`);
    }
  };
  const columns: DataTableColumn<SubscriptionInvoiceView>[] = [
    { id: 'number', headerKey: 'plt:subscription.invoices.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'issued', headerKey: 'plt:subscription.invoices.issued', cell: (row) => <JalaliDate value={row.issuedAt} /> },
    { id: 'kind', headerKey: 'plt:subscription.invoices.kind', cell: (row) => t(`plt:subscription.invoice_kind.${row.kind}`) },
    {
      id: 'period',
      headerKey: 'plt:subscription.invoices.period',
      cell: (row) => t('plt:subscription.period_value', { start: toJalali(row.periodStart), end: toJalali(row.periodEnd) }),
    },
    { id: 'vat', headerKey: 'plt:subscription.invoices.vat', cell: (row) => <Money amountMinor={row.vat.amount} currency={row.vat.currency} /> },
    { id: 'total', headerKey: 'plt:subscription.invoices.total', cell: (row) => <Money amountMinor={row.total.amount} currency={row.total.currency} /> },
    {
      id: 'status',
      headerKey: 'plt:subscription.invoices.status',
      cell: (row) => (
        <StatusChip
          tone={row.status === 'Paid' ? 'active' : row.status === 'Issued' ? 'pending' : 'inactive'}
          labelKey={`plt:subscription.invoice_status.${row.status}`}
        />
      ),
    },
    { id: 'e_invoice', headerKey: 'plt:subscription.invoices.e_invoice', cell: (row) => (row.eInvoiceNumber ? <bdi dir="ltr">{row.eInvoiceNumber}</bdi> : '—') },
    {
      id: 'pdf',
      headerKey: 'plt:subscription.invoices.pdf',
      cell: (row) => (
        <IconButton size="small" aria-label={t('plt:subscription.invoices.download', { number: row.number })} onClick={() => void download(row)} data-testid={`invoice-pdf-${row.number}`}>
          <DownloadOutlinedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="subscription-invoices">
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('plt:subscription.invoices.title')}
      </Typography>
      <DataTable
        captionKey="plt:subscription.invoices.caption"
        columns={columns}
        rows={invoices.data?.items}
        isLoading={invoices.isPending}
        error={invoices.error}
        onRetry={() => void invoices.refetch()}
        getRowId={(row) => row.id}
        emptyTitleKey="plt:subscription.invoices.empty"
      />
    </Paper>
  );
}

export function DemoEnvironmentSection() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [pack, setPack] = useState('');
  const demo = useQuery({
    queryKey: subscriptionKeys.demo(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/tenant/demo-environment')),
    enabled: access.can(MANAGE),
  });
  const packs = useQuery({
    queryKey: ['configuration', 'specialty-packs', session?.tenantId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/specialty-packs')),
    enabled: access.can(MANAGE),
  });
  const create = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/tenant/demo-environment', { body: { packCode: pack || null } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: subscriptionKeys.demo(session?.tenantId) }),
  });
  if (!access.can(MANAGE)) {
    return null;
  }
  const current = demo.data?.demo;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="demo-environment">
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('plt:subscription.demo.title')}
      </Typography>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('plt:subscription.demo.hint')}
        </Typography>
        {demo.isPending ? <LoadingState rows={1} /> : null}
        {demo.error ? <ErrorState error={demo.error} compact /> : null}
        {current ? (
          <Alert severity="success" data-testid="demo-environment-exists">
            {t('plt:subscription.demo.exists', { name: current.displayName, slug: current.slug })}
          </Alert>
        ) : demo.data ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField select label={t('plt:subscription.demo.pack')} value={pack} onChange={(event) => setPack(event.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">{t('plt:subscription.demo.pack_none')}</MenuItem>
              {(packs.data?.items ?? []).map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" onClick={() => create.mutate()} disabled={create.isPending || access.accessMode !== 'Full'} data-testid="demo-environment-create">
              {t('plt:subscription.demo.create')}
            </Button>
          </Stack>
        ) : null}
        {create.error ? <ErrorState error={create.error} compact /> : null}
      </Stack>
    </Paper>
  );
}

export function useSubscriptionOverview() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: subscriptionKeys.overview(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/subscription')),
  });
}
